import React, { useRef } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import type { XYCoord } from 'react-dnd'
import { GripVertical, X, Upload } from 'lucide-react'
import { Input } from '../ui/input'

interface SortableSlideItemProps {
  index: number
  slide: any
  moveSlide: (dragIndex: number, hoverIndex: number) => void
  removeSlide?: () => void
  onFileChange: (file: File) => void
  register: any
  errors: any
  title: string
}

const ItemTypes = {
  SLIDE: 'slide',
}

export function SortableSlideItem({ index, slide, moveSlide, removeSlide, onFileChange, register, errors, title }: SortableSlideItemProps) {
  const ref = useRef<HTMLDivElement>(null)
  
  const [{ handlerId }, drop] = useDrop<
    { index: number },
    void,
    { handlerId: string | symbol | null }
  >({
    accept: ItemTypes.SLIDE,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      }
    },
    hover(item, monitor) {
      if (!ref.current) return
      const dragIndex = item.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) return
      
      const hoverBoundingRect = ref.current?.getBoundingClientRect()
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      const clientOffset = monitor.getClientOffset()
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top
      
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return
      
      moveSlide(dragIndex, hoverIndex)
      item.index = hoverIndex
    },
  })

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.SLIDE,
    item: () => {
      return { index }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  drag(drop(ref))

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      onFileChange(e.target.files[0])
    }
  }

  return (
    <div ref={ref} data-handler-id={handlerId} className={`flex gap-4 p-4 mb-2 border rounded-md bg-white ${isDragging ? 'opacity-50' : 'opacity-100'}`}>
      <div className="flex items-center cursor-move text-slate-400">
        <GripVertical />
      </div>
      
      <div className="flex-1 space-y-4">
        <div className="flex justify-between items-center">
          <h4 className="font-semibold text-sm">{title}</h4>
          {removeSlide && (
            <button type="button" onClick={removeSlide} className="text-red-500 hover:text-red-700">
              <X size={16} />
            </button>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-xs font-medium">Imagen <span className="text-red-500">*</span></label>
            {slide.preview_url ? (
              <div className="relative group border rounded-md p-1 h-32 flex items-center justify-center bg-slate-50">
                <img src={slide.preview_url} alt="preview" className="max-h-full max-w-full object-contain" />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 rounded-md">
                   <label className="cursor-pointer bg-white p-2 rounded-full text-slate-800">
                     <Upload size={16} />
                     <input type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
                   </label>
                </div>
              </div>
            ) : (
              <label className="border-2 border-dashed rounded-md h-32 flex flex-col items-center justify-center cursor-pointer hover:bg-slate-50">
                <Upload className="text-slate-400 mb-2" size={24} />
                <span className="text-xs text-slate-500">Subir imagen</span>
                <input type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
              </label>
            )}
            {errors?.slides?.[index]?.image_url && (
              <p className="text-xs text-red-500">{errors.slides[index].image_url.message}</p>
            )}
            {/* Si no hay imagen pero el usuario trató de guardar, esto muestra error. */}
          </div>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-xs font-medium">URL del CTA (Opcional)</label>
              <Input {...register(`slides.${index}.call_to_action_url`)} placeholder="https://..." />
              {errors?.slides?.[index]?.call_to_action_url && (
                <p className="text-xs text-red-500">{errors.slides[index].call_to_action_url.message}</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id={`active-${index}`} {...register(`slides.${index}.is_active`)} className="rounded border-slate-300 text-primary focus:ring-primary h-4 w-4" />
              <label htmlFor={`active-${index}`} className="text-xs font-medium cursor-pointer">Activo</label>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
